import React from 'react';
import './multi-modal-content.scss';
import { MessageContent } from '@dreamai/types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MessageMetadata } from './threadUtils';
import InMessageArtifacts from './InMessageArtifacts';
import BelowMessageArtifacts from './BelowMessageArtifacts';
import ImageContent from './ImageContent';
import FileContent from './FileContent';
import { useAuth0 } from '@auth0/auth0-react';
import { GetAuthorizationLinkResponse } from '@dreamai/types';
import api from '../../../api';

interface MultiModalContentProps {
  content: MessageContent;
  metadata?: MessageMetadata;
  threadId?: string;
  colors?: Record<string, string>;
}

// Reusable component for rendering text content
const TextContent: React.FC<{
  text: string;
  index?: number;
  metadata?: MessageMetadata;
  threadId?: string;
  colors?: Record<string, string>;
}> = ({ text, index, metadata, threadId, colors }) => {
  const { getAccessTokenSilently } = useAuth0();
  const authRoot = `${process.env.REACT_APP_API_URL}/authorization-links`;

  // Remove any "[Attached: ...]" text and reply syntax "[* ... *]"
  let cleanedText = text.replace(/\n\n\[Attached:.*?\]/g, '');
  // Strip reply syntax prefix if present
  cleanedText = cleanedText.replace(
    /^\[\* .+? .+?\.(?: Context: [^*]+\.)? \*\]\s*/,
    '',
  );
  // Strip FRONTEND_CONTEXT[...] prefix (used for routine creation context etc.)
  cleanedText = cleanedText.replace(/^FRONTEND_CONTEXT\[.*?\]\s*/, '');
  // Strip legacy ["..."] context prefix
  cleanedText = cleanedText.replace(/^\[".*?"\]\s*/, '');

  // Replace {{authRoot}} so authorization links resolve to the API endpoint
  cleanedText = cleanedText.replace(/\{\{authRoot\}\}/g, authRoot);

  // Only render non-empty text
  if (cleanedText.trim() === '') {
    return null;
  }

  return (
    <div
      key={index !== undefined ? `text-${index}` : undefined}
      className='text-content'
      style={colors}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Prevent h2 headings from being rendered - convert to regular paragraphs
          h2: ({ children }) => <p>{children}</p>,
          // You can also prevent other heading levels if needed
          h1: ({ children }) => <p>{children}</p>,
          h3: ({ children }) => <p>{children}</p>,
          h4: ({ children }) => <p>{children}</p>,
          h5: ({ children }) => <p>{children}</p>,
          h6: ({ children }) => <p>{children}</p>,
          a: ({ href, children, ...props }) => {
            if (href && href.startsWith(authRoot)) {
              const key = href.slice(authRoot.length + 1);
              return (
                <a
                  href={href}
                  onClick={async (e) => {
                    e.preventDefault();
                    try {
                      const token = await getAccessTokenSilently();
                      const response =
                        await api.get<GetAuthorizationLinkResponse>(
                          `/authorization-links/${key}`,
                          { headers: { Authorization: `Bearer ${token}` } },
                        );
                      if (response.data.authorizationUrl) {
                        window.open(response.data.authorizationUrl, '_blank');
                      }
                    } catch (err) {
                      console.error(
                        'Failed to resolve authorization link:',
                        err,
                      );
                    }
                  }}
                  {...props}
                >
                  {children}
                </a>
              );
            }
            return (
              <a
                href={href}
                target='_blank'
                rel='noopener noreferrer'
                {...props}
              >
                {children}
              </a>
            );
          },
        }}
      >
        {cleanedText}
      </ReactMarkdown>
      {metadata?.specialMessageArtifacts && threadId && (
        <InMessageArtifacts
          specialMessageArtifacts={metadata.specialMessageArtifacts}
          threadId={threadId}
        />
      )}
    </div>
  );
};

const MultiModalContent: React.FC<MultiModalContentProps> = ({
  content,
  metadata,
  threadId,
  colors,
}) => {
  // First collect all items by type
  const textItems: React.ReactNode[] = [];
  const mediaItems: React.ReactNode[] = [];

  // Check for media items in specialMessageArtifacts first
  if (metadata?.specialMessageArtifacts) {
    metadata.specialMessageArtifacts.forEach((artifact, artifactIndex) => {
      if (artifact.type === 'imagesToView' && artifact.data.images) {
        artifact.data.images.forEach((image, imageIndex) => {
          mediaItems.push(
            <ImageContent
              key={`artifact-image-${artifactIndex}-${imageIndex}`}
              imageUrl={image.url}
              alt={image.caption || image.filename || 'Message attachment'}
            />,
          );
        });
      } else if (artifact.type === 'filesToView' && artifact.data.files) {
        artifact.data.files.forEach((file, fileIndex) => {
          mediaItems.push(
            <FileContent
              key={`artifact-file-${artifactIndex}-${fileIndex}`}
              fileUrl={file.url}
              filename={file.filename}
              mimeType={file.mimeType}
            />,
          );
        });
      }
    });
  }

  // Handle content - if string, convert to text item; if array, process each item
  if (typeof content === 'string') {
    // Add string content as a text item
    const textContent = (
      <TextContent
        key='text-string-content'
        text={content}
        metadata={metadata}
        threadId={threadId}
        colors={colors}
      />
    );
    if (textContent) {
      textItems.push(textContent);
    }
  } else {
    // Process array content
    content.forEach((item, index) => {
      // Handle text content - collect but don't render yet
      if (item.type === 'text') {
        const textContent = (
          <TextContent
            key={`text-${index}`}
            text={item.text as string}
            index={index}
            metadata={metadata}
            threadId={threadId}
            colors={colors}
          />
        );
        if (textContent) {
          textItems.push(textContent);
        }
      }
      // Handle image content
      else if (item.type === 'image_url') {
        const imgRef = item.image_url as string | { url: string };
        const imageUrl = typeof imgRef === 'string' ? imgRef : imgRef.url;

        mediaItems.push(
          <ImageContent
            key={`image-${index}`}
            imageUrl={imageUrl}
            alt='Message attachment'
          />,
        );
      }
      // Handle file content
      else if (item.type === 'file') {
        // Only render file if we have a URL
        if (item.url) {
          const fileMeta = item.metadata as { filename?: string } | undefined;
          mediaItems.push(
            <FileContent
              key={`file-${index}`}
              fileUrl={item.url as string}
              filename={fileMeta?.filename}
              mimeType={item.mime_type as string | undefined}
            />,
          );
        }
      }
    });
  }
  // Render with media items first, then text items, then below-message artifacts
  return (
    <div className='multi-modal-wrapper'>
      {mediaItems}
      {textItems}
      {metadata?.specialMessageArtifacts && threadId && (
        <BelowMessageArtifacts
          specialMessageArtifacts={metadata.specialMessageArtifacts}
          threadId={threadId}
          colors={colors}
        />
      )}
    </div>
  );
};

export default MultiModalContent;
